/**
 * SDK manifest types and data loader.
 * The manifest is generated by python-sdk/scripts/generate_sdk_manifest.py
 * and committed to public/sdk-manifest.json (or served from /sdk-manifest.json).
 */

import manifestData from "../../public/sdk-manifest.json";

// ── Types ──────────────────────────────────────────────────────────────────────

export type MethodRole = "source" | "sink" | "sanitizer" | "neutral";

export interface SDKMethod {
  name: string;
  signature: string;
  description: string;
  role: MethodRole;
  tracks: (string | number)[];
  where_example?: string;
}

export interface SDKClass {
  id: string;
  name: string;
  description: string;
  category: string;
  fqns: string[];
  patterns: string[];
  go_mod?: string;
  pip_snippet?: string;
  import_stmt: string;
  methods: SDKMethod[];
  example_rule?: string;
  rules_using: string[];
  usage_count: number;
}

export interface SDKCategory {
  id: string;
  name: string;
  description: string;
  class_ids: string[];
}

export interface SDKLanguage {
  id: string;
  name: string;
  version: string;
  description: string;
  decorator: string;
  import_prefix: string;
  install: string;
  categories: SDKCategory[];
  classes: Record<string, SDKClass>;
  total_classes: number;
  total_methods: number;
}

export interface SDKManifest {
  version: string;
  generated_at: string;
  languages: Record<string, SDKLanguage>;
}

// ── Accessors ─────────────────────────────────────────────────────────────────

const manifest = manifestData as unknown as SDKManifest;

export function getSDKManifest(): SDKManifest {
  return manifest;
}

export function getSDKLanguages(): SDKLanguage[] {
  return Object.values(manifest.languages);
}

export function getSDKLanguage(id: string): SDKLanguage | undefined {
  return manifest.languages[id];
}

export function getSDKClass(langId: string, classId: string): SDKClass | undefined {
  return manifest.languages[langId]?.classes[classId];
}

export function getAllSDKClasses(): Array<SDKClass & { lang: string }> {
  return Object.entries(manifest.languages).flatMap(([lang, l]) =>
    Object.values(l.classes).map((c) => ({ ...c, lang }))
  );
}

export function getCategoryClasses(lang: SDKLanguage, categoryId: string): SDKClass[] {
  const cat = lang.categories.find((c) => c.id === categoryId);
  if (!cat) return [];
  return cat.class_ids.map((id) => lang.classes[id]).filter(Boolean);
}

// ── UI helpers ────────────────────────────────────────────────────────────────

export const ROLE_CONFIG: Record<MethodRole, { label: string; bg: string; text: string; border: string; dot: string }> = {
  source: {
    label: "Source",
    bg: "bg-green-50",
    text: "text-green-700",
    border: "border-green-200",
    dot: "bg-green-500",
  },
  sink: {
    label: "Sink",
    bg: "bg-red-50",
    text: "text-red-700",
    border: "border-red-200",
    dot: "bg-red-500",
  },
  sanitizer: {
    label: "Sanitizer",
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-200",
    dot: "bg-blue-500",
  },
  neutral: {
    label: "Neutral",
    bg: "bg-gray-50",
    text: "text-gray-500",
    border: "border-gray-200",
    dot: "bg-gray-400",
  },
};

export const LANG_CONFIG: Record<string, { color: string; bg: string; icon: string }> = {
  golang: { color: "text-cyan-700", bg: "bg-cyan-50", icon: "Go" },
  python: { color: "text-yellow-700", bg: "bg-yellow-50", icon: "Py" },
};
